import React, { useEffect, useRef, useState } from "react";
import { ShieldCheck, Info } from "lucide-react";

/**
 * Premium "Verified Creator" badge — shield + checkmark with a green glow,
 * paired with a subtle ℹ️ tooltip explaining the badge.
 *
 * Renders nothing when `verified` is falsy, so callers can drop it next to
 * any name without conditional wrappers.
 */
export default function VerifiedBadge({
  verified,
  size = "sm",         // "xs" | "sm" | "md"
  showInfo = true,
  testIdSuffix = "",
}) {
  if (!verified) return null;

  const dim = size === "xs" ? 12 : size === "md" ? 18 : 14;
  const infoDim = size === "xs" ? 10 : size === "md" ? 13 : 11;
  const wrap = size === "xs" ? "w-4 h-4" : size === "md" ? "w-6 h-6" : "w-5 h-5";

  return (
    <span
      data-testid={`verified-badge${testIdSuffix ? `-${testIdSuffix}` : ""}`}
      className="inline-flex items-center gap-1 align-middle relative shrink-0"
    >
      <span
        aria-label="Verified creator"
        title="Verified Creator"
        className={`${wrap} inline-flex items-center justify-center rounded-full bg-[#00ff88]/12 border border-[#00ff88]/45 text-[#00ff88] cc-verified-glow`}
      >
        <ShieldCheck size={dim} strokeWidth={2.5} className="drop-shadow-[0_0_4px_rgba(0,255,136,0.65)]" />
      </span>
      {showInfo && <VerifiedInfoTip dim={infoDim} testIdSuffix={testIdSuffix} />}
    </span>
  );
}

function VerifiedInfoTip({ dim, testIdSuffix }) {
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const ref = useRef(null);

  // Tap-outside on mobile to close
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("touchstart", onDocClick);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("touchstart", onDocClick);
    };
  }, [open]);

  const show = () => {
    if (timer.current) clearTimeout(timer.current);
    setOpen(true);
  };
  const hideSoon = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpen(false), 120);
  };

  return (
    <span
      ref={ref}
      className="relative inline-flex items-center"
      onMouseEnter={show}
      onMouseLeave={hideSoon}
    >
      <button
        type="button"
        data-testid={`verified-info${testIdSuffix ? `-${testIdSuffix}` : ""}`}
        aria-label="What does verified mean?"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="text-[#606070] hover:text-[#a0a0b0] transition-colors p-0.5 leading-none"
      >
        <Info size={dim} />
      </button>

      {open && (
        <span
          role="tooltip"
          data-testid={`verified-tooltip${testIdSuffix ? `-${testIdSuffix}` : ""}`}
          className="cc-verified-tooltip absolute left-1/2 -translate-x-1/2 top-full mt-2 z-50"
          onMouseEnter={show}
          onMouseLeave={hideSoon}
        >
          <span className="block w-[280px] max-w-[80vw] rounded-xl bg-[#0f0f1a] border border-[#15152a] border-l-2 border-l-[#00ff88] p-4 shadow-[0_18px_40px_-12px_rgba(0,0,0,0.6),0_0_30px_-12px_rgba(0,255,136,0.45)]">
            <span className="flex items-center gap-1.5 text-sm font-semibold text-white">
              Verified Creator
              <ShieldCheck size={13} className="text-[#00ff88]" />
            </span>
            <span className="block text-[12px] leading-relaxed text-[#a0a0b0] mt-1.5">
              This creator has been manually reviewed and verified by the
              CheatCraft team. A verified badge guarantees identity
              authenticity, proven track record, and professional content
              quality. You can order with confidence.
            </span>
          </span>
        </span>
      )}
    </span>
  );
}
